import type { StockTransfer, StockTransferStatus, Warehouse } from '@erp/shared';
import { ArrowLeftRight, Plus } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Badge, Card, Skeleton } from '../../components/shadcn/index.js';
import { Button, DataTable, type DataTableColumn, EmptyState } from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import { formatDateTime } from '../../lib/intl.js';

const STATUS_TONE: Record<StockTransferStatus, 'success' | 'secondary' | 'danger' | 'default'> = {
  received: 'success',
  in_transit: 'default',
  draft: 'secondary',
  cancelled: 'danger',
};

/**
 * Stock transfers between warehouses.
 *
 * The backend has always been able to do this — create, ship, receive, cancel —
 * there was simply no way in from the app. This is that way in: the list.
 * Composing a transfer lives on its own page (/inventory/transfers/new) — an
 * invoice-style grid with server-side item search, because a 9,208-item
 * catalogue does not fit in a dialog's client-side picker.
 *
 * Nothing moves from here. Creating a transfer only writes the intent; the
 * stock does not leave the source warehouse until someone ships it on the
 * detail page, which is the whole point of the two-step: the paperwork is made
 * by whoever plans the move, and the shelf is touched by whoever loads the van.
 */
export function StockTransfersPage(): JSX.Element {
  const { t, i18n } = useTranslation('inventory');
  const navigate = useNavigate();
  const canWrite = usePermission('stock.transfer.write');

  const [rows, setRows] = useState<StockTransfer[] | null>(null);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);

  useEffect(() => {
    void api().get<{ items: StockTransfer[] }>('/stock-transfers').then((r) => setRows(r.data.items)).catch(() => setRows([]));
    // scope=all: this list resolves BOTH ends' names from here, and the far end
    // of a branch keeper's transfer is another branch — narrowed, it rendered
    // blank. It also gates the New button, which a one-branch keeper could
    // never reach.
    void api()
      .get<{ items: Warehouse[] }>('/warehouses', { params: { scope: 'all' } })
      .then((r) => setWarehouses(r.data.items.filter((w) => w.isActive)));
  }, []);

  const warehouseName = useMemo(() => {
    const by = new Map(warehouses.map((w) => [w.id, w.name]));
    return (id: string): string => by.get(id) ?? '—';
  }, [warehouses]);

  if (!rows) return <Card className="p-6"><Skeleton className="h-32 w-full" /></Card>;

  const columns: DataTableColumn<StockTransfer>[] = [
    {
      id: 'transferNumber',
      header: t('transfers.number', { defaultValue: 'Number' }),
      cell: (r) => <span className="font-medium">{r.transferNumber}</span>,
      sortValue: (r) => r.transferNumber,
      searchValue: (r) => r.transferNumber,
    },
    {
      id: 'route',
      header: t('transfers.route', { defaultValue: 'From → To' }),
      cell: (r) => (
        <span className="flex items-center gap-2 text-sm">
          {warehouseName(r.fromWarehouseId)}
          <ArrowLeftRight className="h-3.5 w-3.5 text-fg-muted rtl:rotate-180" aria-hidden />
          {warehouseName(r.toWarehouseId)}
        </span>
      ),
      searchValue: (r) => `${warehouseName(r.fromWarehouseId)} ${warehouseName(r.toWarehouseId)}`,
    },
    {
      id: 'lines',
      header: t('transfers.lines', { defaultValue: 'Items' }),
      cell: (r) => r.lines.length,
      sortValue: (r) => r.lines.length,
      align: 'end',
    },
    {
      id: 'status',
      header: t('transfers.status', { defaultValue: 'Status' }),
      cell: (r) => <Badge variant={STATUS_TONE[r.status]}>{t(`transfers.state.${r.status}`, { defaultValue: r.status })}</Badge>,
      sortValue: (r) => r.status,
      searchValue: (r) => r.status,
    },
    {
      id: 'createdAt',
      header: t('transfers.created', { defaultValue: 'Created' }),
      cell: (r) => formatDateTime(r.createdAt, { locale: i18n.language }),
      sortValue: (r) => r.createdAt,
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            <ArrowLeftRight className="h-6 w-6 text-brand" aria-hidden />
            {t('transfers.title', { defaultValue: 'Stock transfers' })}
          </h1>
          <p className="mt-1 text-sm text-fg-muted">
            {t('transfers.subtitle', {
              defaultValue: 'Move stock from one warehouse to another. Creating a transfer moves nothing — the stock leaves when you ship it.',
            })}
          </p>
        </div>
        {canWrite && warehouses.length >= 2 && (
          <Button onClick={() => navigate('/inventory/transfers/new')}>
            <Plus className="me-1.5 h-4 w-4" aria-hidden />
            {t('transfers.new', { defaultValue: 'New transfer' })}
          </Button>
        )}
      </div>

      {warehouses.length < 2 && (
        <Card className="p-4 text-sm text-fg-muted">
          {t('transfers.needTwo', { defaultValue: 'A transfer needs two warehouses. Add another one first.' })}
        </Card>
      )}

      {rows.length === 0 ? (
        <EmptyState
          icon={<ArrowLeftRight className="h-8 w-8" aria-hidden />}
          title={t('transfers.empty', { defaultValue: 'No transfers yet' })}
          description={t('transfers.emptyHint', { defaultValue: 'Transfers between your warehouses will appear here.' })}
        />
      ) : (
        <Card className="overflow-hidden p-0">
          <DataTable
            data={rows}
            columns={columns}
            rowKey={(r) => r.id}
            searchable
            onRowClick={(r) => navigate(`/inventory/transfers/${r.id}`)}
            initialSort={{ id: 'createdAt', dir: 'desc' }}
          />
        </Card>
      )}
    </div>
  );
}
